// Hypotheses of a case and their append-only assessments (PRD-0075 E, ADR-0095). The path is
// `analytic-hypotheses`: `/hypotheses` is the legacy Deep Research surface.
import type {
  AnalyticHypothesesResponse,
  AnalyticHypothesis,
  AnalyticHypothesisCreate,
  AnalyticHypothesisDetail,
  HypothesisAssessment,
  HypothesisAssessmentCreate,
  HypothesisResearchRun,
  HypothesisResearchRunsResponse,
  LinkedInvestigation,
  LinkedInvestigationsResponse,
} from '@/types/analytic-hypothesis'
import { apiClient } from './client'

const base = (caseId: string) => `/v1/cases/${caseId}/analytic-hypotheses`
const one = (caseId: string, hypothesisId: string) => `${base(caseId)}/${hypothesisId}`

/** *Spis k dátumu*: a read as of an ISO time (PRD-0075 R-D4); omitted, as of now. */
export interface AsOfRead {
  asOf?: string
  signal?: AbortSignal
}

const read = ({ asOf, signal }: AsOfRead = {}) => ({ signal, params: { as_of: asOf } })

export const analyticHypothesesApi = {
  /** Open hypotheses first, then the newest; each with its strip. */
  list: (caseId: string, options?: AsOfRead) =>
    apiClient.get<AnalyticHypothesesResponse>(base(caseId), read(options)),

  /** One hypothesis with its focus entities and every assessment; 404 for an unknown id. */
  get: (caseId: string, hypothesisId: string, options?: AsOfRead) =>
    apiClient.get<AnalyticHypothesisDetail>(one(caseId, hypothesisId), read(options)),

  create: (caseId: string, body: AnalyticHypothesisCreate) =>
    apiClient.post<AnalyticHypothesis>(base(caseId), body),

  close: (caseId: string, hypothesisId: string) =>
    apiClient.post<AnalyticHypothesis>(`${one(caseId, hypothesisId)}/close`),

  reopen: (caseId: string, hypothesisId: string) =>
    apiClient.post<AnalyticHypothesis>(`${one(caseId, hypothesisId)}/reopen`),

  /** A new assessment; there is no way to change one. */
  addAssessment: (caseId: string, hypothesisId: string, body: HypothesisAssessmentCreate) =>
    apiClient.post<HypothesisAssessment>(`${one(caseId, hypothesisId)}/assessments`, body),

  /** Research runs started from the hypothesis, newest first. */
  researchRuns: (caseId: string, hypothesisId: string, options?: AsOfRead) =>
    apiClient.get<HypothesisResearchRunsResponse>(
      `${one(caseId, hypothesisId)}/research-runs`,
      read(options)
    ),

  /** *Spustiť výskum*: Deep Research seeded with the claim and the focus entities. */
  startResearch: (caseId: string, hypothesisId: string) =>
    apiClient.post<HypothesisResearchRun>(`${one(caseId, hypothesisId)}/research-runs`),

  /** The investigations that are evidence for the hypothesis, with their assessment notes. */
  investigations: (caseId: string, hypothesisId: string, options?: AsOfRead) =>
    apiClient.get<LinkedInvestigationsResponse>(
      `${one(caseId, hypothesisId)}/investigations`,
      read(options)
    ),

  /** *Pridať dôkaz*: an investigation of the case becomes evidence for the hypothesis. */
  linkInvestigation: (caseId: string, hypothesisId: string, investigationId: string) =>
    apiClient.put<LinkedInvestigation>(
      `${one(caseId, hypothesisId)}/investigations/${investigationId}`
    ),

  unlinkInvestigation: (caseId: string, hypothesisId: string, investigationId: string) =>
    apiClient.delete<void>(`${one(caseId, hypothesisId)}/investigations/${investigationId}`),
}
